import axios from "axios";
import Api from "../../../../../Api/Api";
import env from "../../../../../Utils/env";

// Interface para dados do CEP
export interface CEPData {
  cep: string;
  logradouro: string;
  complemento: string;
  bairro: string;
  localidade: string;
  uf: string;
  ibge: string;
  gia: string;
  ddd: string;
  siafi: string;
}

// Interface para resposta da API
export interface ApiResponse<T> {
  success: boolean;
  data?: T;
  error?: string;
}

class PagamentoOnlineService {
  private baseURL: string;

  constructor() {
    // Usando a API pública do ViaCEP para busca de CEP
    this.baseURL = "https://viacep.com.br/ws";
  }

  /**
   * Busca dados do CEP na API do ViaCEP
   * @param cep - CEP a ser consultado (com ou sem formatação)
   * @returns Promise com os dados do CEP
   */
  async buscarCEP(cep: string): Promise<ApiResponse<CEPData>> {
    try {
      // Remove formatação do CEP
      const cepLimpo = cep.replace(/[^\d]/g, "");

      // Valida se tem 8 dígitos
      if (cepLimpo.length !== 8) {
        return {
          success: false,
          error: "CEP deve ter 8 dígitos",
        };
      }

      const response = await axios.get(`${this.baseURL}/${cepLimpo}/json/`);

      // Verifica se o CEP foi encontrado
      if (response.data.erro) {
        return {
          success: false,
          error: "CEP não encontrado",
        };
      }

      return {
        success: true,
        data: {
          cep: response.data.cep,
          logradouro: response.data.logradouro,
          complemento: response.data.complemento,
          bairro: response.data.bairro,
          localidade: response.data.localidade,
          uf: response.data.uf,
          ibge: response.data.ibge,
          gia: response.data.gia,
          ddd: response.data.ddd,
          siafi: response.data.siafi,
        },
      };
    } catch (error) {
      console.error("Erro ao buscar CEP:", error);
      return {
        success: false,
        error: "Erro ao consultar CEP. Tente novamente.",
      };
    }
  }

  async verificarCadastroPagamento(cpf: string, hash?: string): Promise<{ existe: boolean; dados?: any; dadosOriginais?: any }> {
    try {
      const cpfLimpo = cpf.replace(/\D/g, '');
      const response = await Api.verificarCadastroPagamento(cpfLimpo, hash);
      if (response.status && response.data) {
        return response.data;
      }
      return { existe: false };
    } catch {
      return { existe: false };
    }
  }

  async buscarEstados(): Promise<any[] | null> {
    const response = await Api.estados();
    return response.data;
  }

  async gerarTokenCartao(payload: any): Promise<any> {
    try {
      if (!payload.card.cvv || payload.card.cvv.trim() === "") {
        return { sucesso: false, erros: ["Código de segurança inválido"] };
      }

      const response = await axios.post(
        "https://api.pagar.me/core/v5/tokens?appId=" + env.PAGARME_PUBLIC_KEY,
        payload,
        {
          headers: {
            "Content-Type": "application/json",
          },
        }
      );

      return {
        status: true,
        token: response.data.id,
      };
    } catch (error: any) {
      if (error.response?.data?.errors) {
        const erros = error.response.data.errors;

        const traducoes: Record<string, string> = {
          "request.card.number": "Número do cartão inválido",
          "request.card.expiration_date": "Data de expiração inválida",
          "request.card.cvv": "Código de segurança inválido",
          "request.card.holder_name": "Nome do titular inválido",
          "request.card": "Número do cartão inválido",
          "request.card.exp_month": "Ano ou Mês de expiração inválido",
          "The number field is not a valid card number":
            "Número do cartão inválido",
          "Card expired.": "Cartão expirado",
          "The cvv field is not a valid cvv.": "Código de segurança inválido",
          "The field cvv must be a string with a minimum length of 3 and a maximum length of 4.":
            "Código de segurança inválido",
          "The holder_name field is required.": "Nome do titular inválido",
          "The number field is not a valid number.":
            "Número do cartão inválido",
          "The field number must be a string with a minimum length of 13 and a maximum length of 19.":
            "Número do cartão inválido",
          "The field exp_month must be between 1 and 12.":
            "Mês de expiração inválido",
        };

        const mensagens = Object.entries(erros).map(([campo, mensagens]) => {
          const msgs = mensagens as string[];
          let mensagem = msgs[0] || campo;

          const traducao = traducoes[mensagem];

          return `${traducao} `;
        });

        return { sucesso: false, erros: mensagens };
      }
    }
  }

  async buscarCidades(idEstado: number): Promise<any[] | null> {
    const response = await Api.cidades(idEstado);
    return response.data;
  }

  /**
   * Valida se um CEP é válido
   * @param cep - CEP a ser validado
   * @returns boolean indicando se é válido
   */
  validarCEP(cep: string): boolean {
    const cepLimpo = cep.replace(/[^\d]/g, "");
    return cepLimpo.length === 8 && /^\d{8}$/.test(cepLimpo);
  }

  /**
   * Formata CEP para exibição (00000-000)
   * @param cep - CEP a ser formatado
   * @returns CEP formatado
   */
  formatarCEP(cep: string): string {
    const cepLimpo = cep.replace(/[^\d]/g, "");
    if (cepLimpo.length === 8) {
      return `${cepLimpo.slice(0, 5)}-${cepLimpo.slice(5)}`;
    }
    return cep;
  }

  /**
   * Busca CEP e preenche automaticamente os campos de endereço
   * @param cep - CEP a ser consultado
   * @returns Objeto com os dados preenchidos para o formulário
   */
  async buscarEPreencherEndereco(cep: string): Promise<{
    success: boolean;
    data?: {
      endereco: string;
      bairro: string;
      cidade: string;
      estado: string;
    };
    error?: string;
  }> {
    const resultado = await this.buscarCEP(cep);

    if (!resultado.success || !resultado.data) {
      return {
        success: false,
        error: resultado.error,
      };
    }

    return {
      success: true,
      data: {
        endereco: resultado.data.logradouro,
        bairro: resultado.data.bairro,
        cidade: resultado.data.localidade,
        estado: resultado.data.uf,
      },
    };
  }
}

// Exporta uma instância singleton da classe
export const pagamentoOnlineService = new PagamentoOnlineService();
export default PagamentoOnlineService;
